// Regras puras da tela de Conexões (WhatsApp, Telegram userbot, bot pessoal).
// Sem React e sem Supabase: testável isoladamente (unit + property + mutation).

export type ConnectionChannel = "whatsapp" | "telegram";
export type ConnectionMethod = "pairing_code" | "qr";

export type ConnectionSessionStatus =
  | "requested"
  | "awaiting_code"
  | "awaiting_password"
  | "awaiting_qr_scan"
  | "connected"
  | "failed"
  | "cancelled"
  | "disconnecting"
  | "disconnected";

/** Linha pública de connection_sessions (o segredo nunca chega ao painel). */
export interface ConnectionSession {
  id: string;
  tenant_id: string;
  channel: ConnectionChannel;
  method: ConnectionMethod | null;
  status: ConnectionSessionStatus;
  pending_action: string | null;
  phone_masked: string | null;
  payload: { pairing_code?: string; qr_data?: string } | null;
  error: string | null;
  retry_after_seconds: number | null;
  expires_at: string;
  created_at: string;
  updated_at: string;
}

/** Status persistido do canal (whatsapp_sessions / mtproto_sessions). */
export interface ChannelSessionRow {
  id?: string | null;
  status: string;
  revoked_reason?: string | null;
  last_health_check_at?: string | null;
}

export type ChannelState = "connected" | "connecting" | "disconnected" | "error";

const IN_FLIGHT: readonly ConnectionSessionStatus[] = [
  "requested",
  "awaiting_code",
  "awaiting_password",
  "awaiting_qr_scan",
];

/** Revogação feita pelo próprio usuário (não é queda). */
export const USER_DISCONNECT_REASON = "user_disconnect";

export function isInFlight(session: Pick<ConnectionSession, "status"> | null | undefined): boolean {
  return !!session && IN_FLIGHT.includes(session.status);
}

/** true quando o bot ainda vai processar a última ação do usuário. */
export function isProcessing(session: Pick<ConnectionSession, "status" | "pending_action"> | null | undefined): boolean {
  return !!session && (session.status === "requested" || session.pending_action !== null);
}

/** Estado exibido no card, combinando sessão persistida + login em andamento. */
export function deriveChannelState(
  row: ChannelSessionRow | null | undefined,
  inflight: Pick<ConnectionSession, "status" | "error"> | null | undefined,
): ChannelState {
  if (inflight && IN_FLIGHT.includes(inflight.status)) {
    return "connecting";
  }
  if (row?.status === "active") {
    return "connected";
  }
  if (inflight?.status === "failed" && inflight.error !== "expired") {
    return "error";
  }
  if (isDroppedSession(row)) {
    return "error";
  }
  return "disconnected";
}

export const CHANNEL_STATE_LABEL: Record<ChannelState, string> = {
  connected: "Conectado",
  connecting: "Conectando",
  disconnected: "Desconectado",
  error: "Erro",
};

/** Sessão caiu sozinha (revogada pelo bot com motivo, não pelo usuário). */
export function isDroppedSession(row: ChannelSessionRow | null | undefined): boolean {
  return (
    row?.status === "revoked" &&
    typeof row.revoked_reason === "string" &&
    row.revoked_reason.length > 0 &&
    row.revoked_reason !== USER_DISCONNECT_REASON
  );
}

/** Monta o E.164 a partir do DDI escolhido e do número digitado. */
export function buildE164(countryCode: string, local: string): string | null {
  const ddi = countryCode.replace(/\D/g, "");
  let digits = local.replace(/\D/g, "");
  if (digits.startsWith(ddi) && digits.length > ddi.length + 9) {
    digits = digits.slice(ddi.length);
  }
  digits = digits.replace(/^0+/, "");
  const full = `+${ddi}${digits}`;
  return /^\+[1-9]\d{7,14}$/.test(full) ? full : null;
}

/** Só dígitos, até `max` (inputs de código). */
export function onlyDigits(value: string, max: number): string {
  return value.replace(/\D/g, "").slice(0, max);
}

/** Código de pareamento WhatsApp legível: "ABCD-1234". */
export function formatPairingCode(code: string | null | undefined): string {
  const clean = (code ?? "").replace(/[^0-9A-Za-z]/g, "").toUpperCase();
  return clean.length === 8 ? `${clean.slice(0, 4)}-${clean.slice(4)}` : clean;
}

/** Segundos até expirar (nunca negativo). */
export function secondsUntil(iso: string, now: number): number {
  const ts = Date.parse(iso);
  if (!Number.isFinite(ts)) return 0;
  return Math.max(0, Math.ceil((ts - now) / 1000));
}

const ERROR_MESSAGES: Record<string, string> = {
  invalid_code: "Código incorreto. Confira no app do Telegram e tente de novo.",
  invalid_password: "Senha de verificação em duas etapas incorreta.",
  code_expired: "O código expirou. Comece de novo para receber outro.",
  expired: "O tempo para concluir a conexão acabou. Comece de novo.",
  phone_invalid: "Número inválido para esta conexão. Confira o DDI e o número.",
  invalid_phone: "Número inválido. Confira o DDI e o número.",
  phone_banned: "Este número está bloqueado pelo Telegram.",
  pairing_failed: "Não foi possível gerar o pareamento agora. Tente de novo em instantes.",
  send_code_failed: "Não foi possível enviar o código agora. Tente de novo em instantes.",
  save_session_failed: "A conexão abriu, mas não conseguimos salvar a sessão. Tente de novo.",
  no_active_challenge: "A conexão foi interrompida. Comece de novo.",
  no_login_in_progress: "A conexão foi interrompida. Comece de novo.",
  risk_acceptance_required: "Confirme que entendeu os riscos para continuar.",
  too_many_requests: "Aguarde alguns segundos antes de tentar de novo.",
  session_not_found: "Esta conexão foi reiniciada em outra aba. Atualize a tela.",
  session_expired: "O tempo para concluir a conexão acabou. Comece de novo.",
  invalid_state: "Esta etapa já foi concluída. Atualize a tela.",
  action_in_progress: "Ainda estamos processando o passo anterior.",
  invalid_code_format: "Digite o código de 5 dígitos que chegou no Telegram.",
  invalid_password_format: "Digite a senha de verificação em duas etapas.",
  rate_limited: "Muitas tentativas de conexão em pouco tempo. Aguarde 15 minutos e tente de novo.",
  rate_limited_daily: "Você atingiu o limite de 20 tentativas de conexão por dia. Tente de novo amanhã.",
  phone_rate_limited: "Este número recebeu muitas tentativas de conexão. Aguarde 1 hora e tente de novo.",
  flood_wait_active: "O Telegram ainda está pedindo uma pausa para este número. Aguarde alguns minutos.",
  monitor_start_failed: "A conta conectou, mas o monitoramento não iniciou. Desconecte e conecte de novo.",
  internal_error: "Algo deu errado do nosso lado. Tente de novo em instantes.",
  qr_refresh_failed: "Não conseguimos renovar o QR Code. Comece de novo.",
  account_not_found: "Esta conta já foi desvinculada. Atualize a tela.",
};

/** Intervalo de refetch de reserva quando o Realtime não está assinado (#9). */
export const REALTIME_FALLBACK_MS = 12_000;

export function fallbackRefetchMs(realtimeStatus: string | null | undefined): number | false {
  return realtimeStatus === "SUBSCRIBED" ? false : REALTIME_FALLBACK_MS;
}

/** Mensagem amigável para o código de erro do bot ou da RPC. */
export function connectionErrorMessage(code: string | null | undefined, retryAfterSeconds?: number | null): string | null {
  if (!code) return null;
  if (code === "flood_wait") {
    return retryAfterSeconds && retryAfterSeconds > 0
      ? `O Telegram pediu uma pausa. Tente de novo em ${formatWait(retryAfterSeconds)}.`
      : "O Telegram pediu uma pausa. Tente de novo em alguns minutos.";
  }
  return ERROR_MESSAGES[code] ?? "Algo deu errado na conexão. Tente de novo.";
}

/** "45 s", "3 min", "2 h". */
export function formatWait(seconds: number): string {
  if (seconds < 60) return `${seconds} s`;
  if (seconds < 3600) return `${Math.ceil(seconds / 60)} min`;
  return `${Math.ceil(seconds / 3600)} h`;
}

/** Extrai o código de erro estável da mensagem de erro do Postgres/PostgREST. */
export function rpcErrorCode(message: string | null | undefined): string {
  const first = (message ?? "").trim().split(/[\s:]/)[0] ?? "";
  return /^[a-z_]+$/.test(first) ? first : "unknown";
}

/** Deep link do bot pessoal com o código de uso único (t.me/<bot>?start=<code>). */
export function buildBotDeepLink(botUsername: string, code: string): string {
  const user = botUsername.replace(/^@/, "");
  return `https://t.me/${encodeURIComponent(user)}?start=${encodeURIComponent(code)}`;
}

/** Chave de dispensa do alerta de queda: muda a cada nova queda (#13). */
export function dropDismissKey(channel: string, row: ChannelSessionRow): string {
  return ["lucralink:drop-dismissed", channel, row.id ?? "", row.revoked_reason ?? "", row.last_health_check_at ?? ""].join(":");
}
